import React, { useEffect, useMemo, useState } from "react"
import { CalendarPlus, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, Download, ExternalLink, MessageCircle, Plus, Search, Settings, Shirt, Trash2, Trophy, Upload, UsersRound, X } from "lucide-react"
import { api, download, json, pollJob } from "../api"
import { Link, useNavigate, useParams } from "../router"
import { Badge, Button, Card, EmptyState, ErrorNotice, PageHeader, TeamMark } from "../ui"
import { GroupMePanel } from "./GroupMePanel"

const tabs = [
  ["overview", "Overview", Trophy],
  ["roster", "Roster", UsersRound],
  ["schedule", "Schedule", CalendarPlus],
  ["imports", "Import", Upload],
  ["messages", "GroupMe", MessageCircle],
  ["settings", "Settings", Settings],
]

export function TeamPage() {
  const { id } = useParams("/teams/:id")
  const navigate = useNavigate()
  const [team, setTeam] = useState(null)
  const [tab, setTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get("tab")
    return tabs.some(([key]) => key === requestedTab) ? requestedTab : "overview"
  })
  const [form, setForm] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const reload = () => api(`/teams/${id}`).then(setTeam).catch(setError)
  useEffect(() => { reload() }, [id])

  const upcoming = useMemo(() => {
    if (!team) return []
    return [
      ...team.games.map(item => ({ ...item, eventType: "game", date: item.game_date })),
      ...team.practices.map(item => ({ ...item, eventType: "practice", date: item.practice_date })),
    ].sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [team])

  async function submitNested(event, type) {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const values = Object.fromEntries(formData)
    const singular = type === "players" ? "player" : type === "games" ? "game" : "practice"
    if (type === "players") {
      values.batting_ability = 3; values.fielding_ability = 3
      values.can_catch = values.can_catch === "on"; values.can_pitch = values.can_pitch === "on"
      values.preferred_positions = []
    }
    if (type === "games") {
      values.innings = Number(values.innings)
      values.game_date = new Date(values.game_date).toISOString()
    }
    if (type === "practices") {
      values.duration_minutes = Number(values.duration_minutes)
      values.practice_date = new Date(values.practice_date).toISOString()
      values.recurring = formData.has("recurring")
      values.selected_days = formData.getAll("selected_days")
    }
    try {
      await api(`/teams/${id}/${type}`, json("POST", { [singular]: values }))
      setForm(null); await reload()
    } catch (caught) { setError(caught) }
  }

  async function saveTeam(event) {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    values.has_fourth_outfielder = values.has_fourth_outfielder === "on"
    values.enable_snack_schedule = values.enable_snack_schedule === "on"
    try {
      const updated = await api(`/teams/${id}`, json("PATCH", { team: values }))
      setTeam(current => ({ ...current, ...updated }))
    } catch (caught) { setError(caught) }
  }

  async function deleteTeam() {
    if (!window.confirm(`Delete ${team.name} and all of its data?`)) return
    try { await api(`/teams/${id}`, { method: "DELETE" }); navigate("/teams") } catch (caught) { setError(caught) }
  }

  function selectTab(nextTab) {
    setTab(nextTab)
    setForm(null)
    const url = new URL(window.location.href)
    if (nextTab === "overview") url.searchParams.delete("tab")
    else url.searchParams.set("tab", nextTab)
    window.history.replaceState({}, "", `${url.pathname}${url.search}`)
  }

  if (!team) return <div className="page"><p>Loading team…</p><ErrorNotice error={error} /></div>

  return (
    <div className="page">
      <Link className="back-link" to="/teams"><ChevronLeft size={17} /> All teams</Link>
      <PageHeader eyebrow={team.season} title={team.name} copy={`${team.players.length} players · ${team.games.length} games · ${team.practices.length} practices`} actions={<TeamMark team={team} size="xl" />} />
      <nav className="tabs" aria-label="Team sections">{tabs.map(([key, label, Icon]) => <button key={key} className={tab === key ? "tabs__active" : ""} onClick={() => selectTab(key)}><Icon size={17} />{label}</button>)}</nav>
      <ErrorNotice error={error} />

      {tab === "overview" ? <Overview team={team} upcoming={upcoming} onNavigate={selectTab} /> : null}
      {tab === "roster" ? <Roster team={team} form={form} setForm={setForm} onSubmit={event => submitNested(event, "players")} onExport={() => download(`/teams/${team.id}/roster.csv`, `${team.slug}-roster.csv`).catch(setError)} /> : null}
      {tab === "schedule" ? <Schedule events={upcoming} form={form} setForm={setForm} onGame={event => submitNested(event, "games")} onPractice={event => submitNested(event, "practices")} /> : null}
      {tab === "imports" ? <Imports team={team} onComplete={reload} onError={setError} /> : null}
      {tab === "messages" ? <GroupMePanel team={team} onError={setError} /> : null}
      {tab === "settings" ? <TeamSettings team={team} onSubmit={saveTeam} onDelete={deleteTeam} onLogoUploaded={updated => setTeam(current => ({ ...current, ...updated }))} onError={setError} /> : null}
    </div>
  )
}

function Overview({ team, upcoming, onNavigate }) {
  const nextEvent = upcoming.find(event => new Date(event.date) >= new Date())
  return <div className="dashboard-grid"><Card className="hero-card" style={{ "--team-color": team.primary_color }}><p className="eyebrow">Next team event</p>{nextEvent ? <><Badge tone={nextEvent.eventType === "game" ? "orange" : "blue"}>{nextEvent.eventType}</Badge><h2>{nextEvent.eventType === "game" ? `vs ${nextEvent.opponent}` : "Team practice"}</h2><p>{formatDate(nextEvent.date)}{nextEvent.location ? ` · ${nextEvent.location}` : ""}</p><Link className="button button--secondary" to={nextEvent.eventType === "game" ? `/games/${nextEvent.id}` : `/practices/${nextEvent.id}`}>Open event</Link></> : <EmptyState icon={CalendarPlus} title="Nothing scheduled" copy="Add the first game or practice for this season." />}</Card><Card className="panel"><div className="panel__header"><div><p className="eyebrow">Season readiness</p><h2>Team checklist</h2></div></div><button className="checklist-row" onClick={() => onNavigate("roster")}><span className={team.players.length ? "checklist-dot checklist-dot--done" : "checklist-dot"}>{team.players.length ? "✓" : "1"}</span><span><strong>Build the roster</strong><small>{team.players.length ? `${team.players.length} players ready` : "Add players and parent contacts"}</small></span><span>→</span></button><button className="checklist-row" onClick={() => onNavigate("schedule")}><span className={upcoming.length ? "checklist-dot checklist-dot--done" : "checklist-dot"}>{upcoming.length ? "✓" : "2"}</span><span><strong>Plan the schedule</strong><small>{upcoming.length ? `${upcoming.length} events scheduled` : "Add games and practices"}</small></span><span>→</span></button></Card></div>
}

function Roster({ team, form, setForm, onSubmit, onExport }) {
  const [query, setQuery] = useState("")
  const normalizedQuery = query.trim().toLowerCase()
  const players = [...team.players]
    .sort((a, b) => (a.jersey_number ?? 999) - (b.jersey_number ?? 999) || a.first_name.localeCompare(b.first_name))
    .filter(player => {
      if (!normalizedQuery) return true
      const searchable = [player.first_name, player.last_name, player.jersey_number, player.parent_name_1, player.parent_name_2]
        .filter(value => value != null)
        .join(" ")
        .toLowerCase()
      return searchable.includes(normalizedQuery)
    })

  return (
    <Card className="panel roster-panel">
      <div className="panel__header roster-panel__header">
        <div>
          <p className="eyebrow">Player directory</p>
          <h2>Roster</h2>
          <p className="roster-panel__summary">{team.players.length} {team.players.length === 1 ? "player" : "players"} on this team</p>
        </div>
        <Button type="button" onClick={() => setForm("player")}><Plus size={16} /> Add player</Button>
      </div>

      {form === "player" ? <form className="subform" onSubmit={onSubmit}><button type="button" className="icon-button subform__close" aria-label="Close add player form" onClick={() => setForm(null)}><X /></button><div className="form-grid form-grid--3"><label>First name<input name="first_name" required /></label><label>Last name<input name="last_name" /></label><label>Jersey number<input name="jersey_number" type="number" min="0" /></label></div><div className="form-grid"><label>Parent / guardian<input name="parent_name_1" /></label><label>Phone<input name="parent_phone_1" type="tel" /></label></div><div className="check-pair"><label className="check-row"><input name="can_pitch" type="checkbox" /> Can pitch</label><label className="check-row"><input name="can_catch" type="checkbox" /> Can catch</label></div><Button type="submit">Add to roster</Button></form> : null}

      {team.players.length ? <>
        <div className="roster-toolbar">
          <label className="roster-search">
            <span className="sr-only">Search players</span>
            <Search size={16} aria-hidden="true" />
            <input name="roster_search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search players" aria-label="Search players" />
          </label>
          <p className="roster-results" aria-live="polite">Showing {players.length} of {team.players.length}</p>
          <Button type="button" variant="secondary" onClick={onExport}><Download size={16} /> Export roster</Button>
        </div>

        {players.length ? <div className="roster-table">
          <div className="roster-table__head"><span>Player</span><span>Roster details</span><span className="sr-only">Action</span></div>
          {players.map(player => <div className="roster-table__row" key={player.id}>
            <span className="player-cell">
              <span className="player-avatar">{player.first_name[0]}{player.last_name?.[0]}</span>
              <span className="player-cell__body"><strong>{player.first_name} {player.last_name}</strong><small>{player.parent_name_1 || "Active player"}</small></span>
            </span>
            <span className="roster-meta">
              <span className="roster-jersey">{player.jersey_number != null ? `#${player.jersey_number}` : "No jersey"}</span>
              {player.can_pitch ? <Badge>Pitcher</Badge> : null}
              {player.can_catch ? <Badge>Catcher</Badge> : null}
            </span>
            <Link className="roster-action" to={`/players/${player.id}`}>View &amp; edit <ChevronRight size={16} /></Link>
          </div>)}
        </div> : <EmptyState icon={Search} title="No players found" copy={`No roster entries match “${query.trim()}”.`} />}
      </> : form !== "player" ? <EmptyState icon={Shirt} title="The roster is empty" copy="Add the first player to begin availability and lineup planning." /> : null}
    </Card>
  )
}

function Schedule({ events, form, setForm, onGame, onPractice }) {
  return <Card className="panel"><div className="panel__header"><div><p className="eyebrow">Season calendar</p><h2>Games & practices</h2></div><div className="button-row"><Button variant="secondary" onClick={() => setForm("practice")}><Plus size={16} /> Practice</Button><Button onClick={() => setForm("game")}><Plus size={16} /> Game</Button></div></div>{form === "game" ? <EventForm type="game" onClose={() => setForm(null)} onSubmit={onGame} /> : null}{form === "practice" ? <EventForm type="practice" onClose={() => setForm(null)} onSubmit={onPractice} /> : null}<div className="timeline">{events.map(event => <Link key={`${event.eventType}-${event.id}`} className="timeline-row" to={event.eventType === "game" ? `/games/${event.id}` : `/practices/${event.id}`}><span className={`timeline-dot timeline-dot--${event.eventType}`} /><span className="event-date"><strong>{new Date(event.date).getDate()}</strong>{new Date(event.date).toLocaleDateString([], { month: "short" })}</span><span><Badge tone={event.eventType === "game" ? "orange" : "blue"}>{event.eventType}</Badge><strong className="block">{event.eventType === "game" ? `vs ${event.opponent}` : "Team practice"}</strong><small>{formatDate(event.date, false)}{event.field_number ? ` · Field ${event.field_number}` : event.location ? ` · ${event.location}` : ""}</small></span><span>→</span></Link>)}</div>{!events.length && !form ? <EmptyState icon={ClipboardList} title="No events scheduled" copy="Add a game or practice to begin planning the season." /> : null}</Card>
}

function EventForm({ type, onClose, onSubmit }) {
  return <form className="subform" onSubmit={onSubmit}><button type="button" className="icon-button subform__close" onClick={onClose}><X /></button><div className="form-grid form-grid--3">{type === "game" ? <label>Opponent<input name="opponent" required /></label> : <label>Location<input name="location" /></label>}<label>Date and time<input name={type === "game" ? "game_date" : "practice_date"} type="datetime-local" required /></label>{type === "game" ? <label>Innings<input name="innings" type="number" min="1" defaultValue="6" required /></label> : <label>Duration (minutes)<input name="duration_minutes" type="number" min="15" defaultValue="90" required /></label>}</div>{type === "game" ? <label>Field number<input name="field_number" type="number" min="1" /></label> : <><label>Notes<textarea name="notes" rows="3" /></label><label className="check-row"><input name="recurring" type="checkbox" /> Create a recurring practice series</label><div className="form-grid"><fieldset><legend>Repeat on</legend><div className="badge-row">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => <label className="check-row" key={day}><input name="selected_days" type="checkbox" value={index} /> {day}</label>)}</div></fieldset><label>Repeat through<input name="recurrence_end_date" type="date" /></label></div></>}<Button type="submit">Add {type}</Button></form>
}

function TeamSettings({ team, onSubmit, onDelete, onLogoUploaded, onError }) {
  const [logo, setLogo] = useState(null)
  const [logoNotice, setLogoNotice] = useState(null)
  async function uploadLogo() {
    if (!logo) return
    try {
      const body = new FormData()
      body.append("logo", logo)
      const updated = await api(`/teams/${team.id}/logo`, { method: "POST", body })
      onLogoUploaded(updated)
      setLogo(null)
      setLogoNotice("Logo uploaded. It now appears across your team admin pages.")
    } catch (caught) {
      onError(caught)
    }
  }
  return (
    <div className="team-settings-shell">
      <div className="team-settings-layout">
        <Card className="panel team-settings-main">
        <div className="panel__header team-settings-heading">
          <div>
            <p className="eyebrow">Team configuration</p>
            <h2>Team settings</h2>
            <p>Manage the details coaches see and the defaults used for new lineups.</p>
          </div>
        </div>

        <form className="form-stack settings-form team-settings-form" onSubmit={onSubmit}>
          <section className="team-settings-section" aria-labelledby="team-details-heading">
            <div className="settings-section-heading">
              <h3 id="team-details-heading">Team details</h3>
              <p>Keep the team name, season, timezone, and colors current.</p>
            </div>
            <div className="form-grid">
              <label htmlFor="team-name">Team name<input id="team-name" name="name" defaultValue={team.name} required /></label>
              <label htmlFor="team-season">Season<input id="team-season" name="season" defaultValue={team.season} required /></label>
            </div>
            <label htmlFor="team-timezone">Timezone<span className="select-control"><select id="team-timezone" name="timezone" defaultValue={team.timezone}><option>America/New_York</option><option>America/Chicago</option><option>America/Denver</option><option>America/Los_Angeles</option></select><ChevronDown size={16} aria-hidden="true" /></span></label>
            <div className="form-grid settings-color-grid">
              <label className="settings-color-field" htmlFor="team-primary-color"><input id="team-primary-color" name="primary_color" type="color" defaultValue={team.primary_color} /><span><strong>Primary color</strong><small>Used for the team mark and accents.</small></span></label>
              <label className="settings-color-field" htmlFor="team-accent-color"><input id="team-accent-color" name="secondary_color" type="color" defaultValue={team.secondary_color} /><span><strong>Accent color</strong><small>Used for supporting highlights.</small></span></label>
            </div>
          </section>

          <section className="team-settings-section" aria-labelledby="team-options-heading">
            <div className="settings-section-heading">
              <h3 id="team-options-heading">Season options</h3>
              <p>Choose the extra tools and positions this team needs.</p>
            </div>
            <div className="settings-toggle-list">
              <label className="settings-toggle">
                <span><strong>Use a fourth outfielder</strong><small>Add an extra outfield position when generating lineups.</small></span>
                <span className="toggle-control"><span aria-hidden="true" /><input name="has_fourth_outfielder" type="checkbox" defaultChecked={team.has_fourth_outfielder} aria-label="Use a fourth outfielder" /></span>
              </label>
              <label className="settings-toggle">
                <span><strong>Enable snack signups</strong><small>Let families claim snack duty for scheduled games.</small></span>
                <span className="toggle-control"><span aria-hidden="true" /><input name="enable_snack_schedule" type="checkbox" defaultChecked={team.enable_snack_schedule} aria-label="Enable snack signups" /></span>
              </label>
            </div>
          </section>

          <section className="team-settings-section" aria-labelledby="team-coaching-heading">
            <div className="settings-section-heading">
              <h3 id="team-coaching-heading">Coaching defaults</h3>
              <p>These instructions are included whenever a new lineup is generated.</p>
            </div>
            <label htmlFor="team-coaching-preferences">Coaching preferences<textarea id="team-coaching-preferences" name="default_coaching_preferences" defaultValue={team.default_coaching_preferences || ""} rows="4" placeholder="Example: Rotate players through the infield and prioritize development." /></label>
            <label htmlFor="team-lineup-rules">Lineup rules<textarea id="team-lineup-rules" name="default_lineup_rules" defaultValue={team.default_lineup_rules || ""} rows="4" placeholder="Example: No player sits twice before everyone has sat once." /></label>
          </section>

          <div className="team-settings-actions">
            <p>Changes apply to this team only.</p>
            <Button type="submit">Save changes</Button>
          </div>
        </form>
        </Card>

        <aside className="team-settings-aside">
        <Card className="panel team-logo-panel">
          <div className="team-logo-preview">
            <TeamMark team={team} size="xl" />
            <div><p className="eyebrow">Team identity</p><h3>{team.name}</h3><p>{team.logo_url ? "Your logo is used across team pages." : "Add a logo to make team pages easier to recognize."}</p></div>
          </div>
          <label className="team-logo-upload" htmlFor="team-logo"><span>Choose a new logo</span><input id="team-logo" name="logo" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={event => setLogo(event.target.files[0])} /></label>
          <Button type="button" variant="secondary" className="team-logo-button" onClick={uploadLogo} disabled={!logo}><Upload size={16} /> Upload logo</Button>
          {logoNotice ? <p className="saved-note" role="status">{logoNotice}</p> : null}
        </Card>

        <Card className="panel team-resources-panel">
          <div className="settings-section-heading">
            <h3>Share this team</h3>
            <p>Open the public schedule or subscribe to its calendar.</p>
          </div>
          <div className="team-resource-links">
            <a href={`/pages/${team.slug}`} target="_blank" rel="noreferrer"><span><strong>Public team page</strong><small>View the family-facing page.</small></span><ExternalLink size={16} aria-hidden="true" /></a>
            <a href={`/api/v1/public/teams/${team.slug}/calendar.ics`}><span><strong>Calendar feed</strong><small>Subscribe from a calendar app.</small></span><Download size={16} aria-hidden="true" /></a>
          </div>
        </Card>

        <section className="team-danger-zone" aria-labelledby="delete-team-heading">
          <div><h3 id="delete-team-heading">Delete team</h3><p>Permanently remove this team and all of its related data.</p></div>
          <Button type="button" variant="danger" onClick={onDelete}><Trash2 size={16} /> Delete team</Button>
        </section>
        </aside>
      </div>
    </div>
  )
}

function Imports({ team, onComplete, onError }) {
  const [kind, setKind] = useState("roster")
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState([])
  const [busy, setBusy] = useState(false)
  async function previewFile() {
    if (!file) return
    setBusy(true)
    try {
      const body = new FormData()
      body.append("file", file)
      let result = await api(`/teams/${team.id}/imports/${kind}/preview`, { method: "POST", body })
      if (result.status_url) result = await pollJob(result.status_url)
      setPreview(result[kind === "roster" ? "players" : "games"])
    } catch (caught) { onError(caught) } finally { setBusy(false) }
  }
  async function commit() {
    setBusy(true)
    try {
      await api(`/teams/${team.id}/imports/${kind}`, json("POST", { [kind === "roster" ? "players" : "games"]: preview, file_name: file?.name }))
      setPreview([]); setFile(null); await onComplete()
    } catch (caught) { onError(caught) } finally { setBusy(false) }
  }
  const recordLabel = kind === "roster" ? (preview.length === 1 ? "player" : "players") : (preview.length === 1 ? "game" : "games")

  return (
    <Card className="panel import-panel">
      <div className="panel__header"><div><p className="eyebrow">Bulk setup</p><h2>Import roster or schedule</h2></div></div>
      <div className="segmented">
        <button className={kind === "roster" ? "segmented__active" : ""} onClick={() => { setKind("roster"); setPreview([]) }}>Roster</button>
        <button className={kind === "schedule" ? "segmented__active" : ""} onClick={() => { setKind("schedule"); setPreview([]) }}>Schedule</button>
      </div>
      <p className="muted">Upload a CSV or Excel sheet, a PDF, or a clear photo. Documents and photos are extracted securely by the Rails server with OpenAI, then held for your review before anything is saved.</p>
      <div className="form-stack import-source">
        <label>Document or image<input type="file" accept=".csv,.xlsx,.xls,.pdf,image/png,image/jpeg,image/heic,image/webp" onChange={event => { setFile(event.target.files[0]); setPreview([]) }} /></label>
        {!preview.length ? <div className="button-row"><Button onClick={previewFile} disabled={!file || busy}>{busy ? "Extracting…" : "Preview import"}</Button></div> : null}
      </div>

      {preview.length ? (
        <section className="import-review" aria-labelledby="import-review-heading">
          <div className="import-review__header">
            <div>
              <p className="eyebrow">Ready to import</p>
              <h3 id="import-review-heading">Review {preview.length} extracted {recordLabel}</h3>
              <p>Check every field below. Nothing will be saved until you import these records.</p>
            </div>
            <div className="button-row import-review__actions">
              <Button onClick={commit} disabled={busy}>{busy ? "Importing…" : `Import ${preview.length} ${recordLabel}`}</Button>
              <Button variant="secondary" onClick={previewFile} disabled={!file || busy}>Refresh preview</Button>
            </div>
          </div>
          <div className="import-preview-list">
            {preview.map((item, index) => kind === "roster"
              ? <RosterPreviewRecord item={item} index={index} key={index} />
              : <SchedulePreviewRecord item={item} index={index} key={index} />)}
          </div>
        </section>
      ) : null}
    </Card>
  )
}

function RosterPreviewRecord({ item, index }) {
  const name = [item.first_name, item.last_name].filter(Boolean).join(" ") || `Player ${index + 1}`
  return (
    <article className="import-preview-card">
      <div className="import-preview-card__heading"><span>{index + 1}</span><div><p>Player</p><h4>{name}</h4></div></div>
      <dl className="import-preview-fields">
        <PreviewField label="Jersey number" value={item.jersey_number} />
        <PreviewField label="Batting ability" value={rating(item.batting_ability)} />
        <PreviewField label="Fielding ability" value={rating(item.fielding_ability)} />
        <PreviewField label="Can pitch" value={yesOrNo(item.can_pitch)} />
        <PreviewField label="Can catch" value={yesOrNo(item.can_catch)} />
        <PreviewField label="Preferred positions" value={item.preferred_positions?.join(", ")} />
        <PreviewField label="Parent or guardian 1" value={item.parent_name_1} />
        <PreviewField label="Phone 1" value={item.parent_phone_1} />
        <PreviewField label="Parent or guardian 2" value={item.parent_name_2} />
        <PreviewField label="Phone 2" value={item.parent_phone_2} />
        <PreviewField label="Notes" value={item.notes} wide />
      </dl>
    </article>
  )
}

function SchedulePreviewRecord({ item, index }) {
  return (
    <article className="import-preview-card">
      <div className="import-preview-card__heading"><span>{index + 1}</span><div><p>Game</p><h4>vs {item.opponent}</h4></div></div>
      <dl className="import-preview-fields import-preview-fields--schedule">
        <PreviewField label="Date and time" value={item.game_date ? formatDate(item.game_date) : null} wide />
        <PreviewField label="Field number" value={item.field_number} />
        <PreviewField label="Innings" value={item.innings} />
      </dl>
    </article>
  )
}

function PreviewField({ label, value, wide = false }) {
  const present = value !== null && value !== undefined && value !== ""
  return <div className={wide ? "import-preview-field import-preview-field--wide" : "import-preview-field"}><dt>{label}</dt><dd className={present ? "" : "import-preview-field__empty"}>{present ? value : "Not found"}</dd></div>
}

function rating(value) {
  return value ? `${value} of 5` : null
}

function yesOrNo(value) {
  return value === true ? "Yes" : value === false ? "No" : null
}

function formatDate(value, includeDate = true) {
  return new Date(value).toLocaleString([], includeDate ? { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" } : { hour: "numeric", minute: "2-digit" })
}
